import { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Pagination } from '@/components/common/Pagination';
import { EmptyState } from '@/components/common/EmptyState';
import { useDispatchesList } from '@/hooks/useDispatches';
import type { DispatchStatus } from '@/types/api.types';
import { Send } from 'lucide-react';

const PAGE_SIZE = 20;

const ALL_STATUSES: DispatchStatus[] = ['pending', 'sent', 'delivered', 'read', 'failed'];

const STATUS_LABEL: Record<DispatchStatus, string> = {
  pending: 'Pendente',
  sent: 'Enviado',
  delivered: 'Entregue',
  read: 'Lido',
  failed: 'Falhou',
};

const STATUS_VARIANT: Record<DispatchStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  pending: 'outline',
  sent: 'secondary',
  delivered: 'default',
  read: 'default',
  failed: 'destructive',
};

interface Props {
  campaignId: string;
}

function DispatchesSkeleton() {
  return (
    <div className="space-y-3 p-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4">
          <Skeleton className="h-5 w-36" />
          <Skeleton className="h-5 flex-1" />
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-5 w-24" />
        </div>
      ))}
    </div>
  );
}

export function CampaignDispatchesTable({ campaignId }: Props) {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<DispatchStatus | undefined>();

  const { data, isLoading } = useDispatchesList({
    campaignId,
    status: statusFilter,
    page,
    pageSize: PAGE_SIZE,
  });

  const dispatches = data?.data ?? [];
  const meta = data?.meta;

  return (
    <div className="space-y-3">
      {/* Status filter pills */}
      <div className="flex flex-wrap gap-1">
        <button
          onClick={() => { setStatusFilter(undefined); setPage(1); }}
          className={`rounded-full px-3 py-1 text-xs font-medium transition-colors border ${
            !statusFilter ? 'bg-primary text-primary-foreground border-primary' : 'border-input text-muted-foreground hover:text-foreground'
          }`}
        >
          Todos
        </button>
        {ALL_STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => { setStatusFilter(s); setPage(1); }}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors border ${
              statusFilter === s ? 'bg-primary text-primary-foreground border-primary' : 'border-input text-muted-foreground hover:text-foreground'
            }`}
          >
            {STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      {isLoading ? (
        <DispatchesSkeleton />
      ) : dispatches.length === 0 ? (
        <EmptyState icon={Send} title="Nenhum disparo encontrado" />
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Destinatário</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Enviado em</TableHead>
                <TableHead>Erro</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dispatches.map((d) => (
                <TableRow key={d.id}>
                  <TableCell>
                    <p className="font-mono text-xs">{d.contactPhone}</p>
                    {d.contactName && (
                      <p className="text-xs text-muted-foreground">{d.contactName}</p>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={STATUS_VARIANT[d.status]}>{STATUS_LABEL[d.status]}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {d.sentAt
                      ? format(new Date(d.sentAt), "dd/MM 'às' HH:mm", { locale: ptBR })
                      : '—'}
                  </TableCell>
                  <TableCell className="max-w-[200px] truncate text-xs text-destructive">
                    {d.errorMessage ?? '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {meta && (
            <Pagination
              page={page}
              totalPages={meta.totalPages}
              total={meta.total}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          )}
        </>
      )}
    </div>
  );
}
